// Email magic-link (passwordless) auth. Thin, mockable wrapper.

import {
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  onAuthStateChanged,
  signOut as fbSignOut,
} from "firebase/auth";

import { auth, isConfigured } from "./init.js";
import { PUBLIC_BASE_URL } from "./config.js";

const EMAIL_STORAGE_KEY = "einvite:pendingEmail";

function ensureReady() {
  if (!isConfigured || !auth) {
    throw new Error("Firebase is not configured. Fill frontend/.env first.");
  }
}

// Where the magic link returns the user. We bring them back to /auth/finish,
// preserving any slug they were claiming via the `next` query param.
function actionCodeSettings(next) {
  const url = new URL("/auth/finish", PUBLIC_BASE_URL);
  if (next) url.searchParams.set("next", next);
  return { url: url.toString(), handleCodeInApp: true };
}

export async function sendMagicLink(email, next) {
  ensureReady();
  await sendSignInLinkToEmail(auth, email, actionCodeSettings(next));
  window.localStorage.setItem(EMAIL_STORAGE_KEY, email);
}

export function isMagicLink(href = window.location.href) {
  if (!isConfigured || !auth) return false;
  return isSignInWithEmailLink(auth, href);
}

// Completes sign-in when the user lands on /auth/finish via the email link.
export async function completeMagicLink(href = window.location.href) {
  ensureReady();
  let email = window.localStorage.getItem(EMAIL_STORAGE_KEY);
  if (!email) {
    // User opened the link on a different device; ask them to re-enter it.
    email = window.prompt("Confirm your email to finish signing in:");
  }
  const cred = await signInWithEmailLink(auth, email, href);
  window.localStorage.removeItem(EMAIL_STORAGE_KEY);
  return cred.user;
}

export function watchAuth(cb) {
  if (!isConfigured || !auth) {
    cb(null);
    return () => {};
  }
  return onAuthStateChanged(auth, cb);
}

export async function signOut() {
  ensureReady();
  await fbSignOut(auth);
}
